import { useEffect, useRef, useState } from 'react';

import { DexieDb, Db } from '@shared/storage';
import { report } from '@shared/observability';

/**
 * React hook that keep track of the window height
 *
 */
export function useResize() {
	useEffect(() => {
		const r = document.querySelector(':root');
		if (!r) {
			return;
		}

		const cb = () => {
			const height = window.innerHeight;
			document.documentElement.style.setProperty(
				'--window-height',
				`${height}px`,
			);
		};
		cb();
		window.addEventListener('resize', cb);
		window.addEventListener('orientationchange', cb);
		return () => {
			window.removeEventListener('resize', cb);
			window.removeEventListener('orientationchange', cb);
		};
	}, []);
}

/**
 * React hook that returns if the PWA was installed
 *
 * @return if the pwa is installed
 *
 */
export function useInstalled(): boolean {
	const [isInstalled, setIsInstalled] = useState(false);
	useEffect(() => {
		if (window.matchMedia('(display-mode: standalone)').matches) {
			setIsInstalled(true);
		}
	}, []);
	return isInstalled;
}

/**
 * React hook that returns the database
 *
 * @param args - Props to get issues
 *
 */
export function useDb(): DbResponse {
	const [db, setDb] = useState<Db | null>(null);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		const database = new DexieDb();
		database
			.start()
			.then(() => {
				setDb(database);
			})
			.catch((err: Error) => {
				setError(err);
				// The store this reports to is itself IndexedDB -- if it's
				// also unavailable, local-reporter's in-memory fallback tier
				// is what keeps this from being the one failure that can
				// never be recorded. See
				// improvements/16-observability-error-handling.md's "the one
				// failure mode this whole design has to survive."
				report({ name: 'db_open_failure' });
			});
	}, []);

	return {
		db,
		error,
	};
}

interface DbResponse {
	db: Db | null;
	error: Error | null;
}

export function useInterval(callback: () => void, delay: number) {
	// Creating a ref
	const savedCallback = useRef(callback);

	// To remember the latest callback .
	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	// combining the setInterval and
	//clearInterval methods based on delay.
	useEffect(() => {
		savedCallback.current();
		function func() {
			savedCallback.current();
		}
		if (delay !== null) {
			const id = setInterval(func, delay);
			return () => clearInterval(id);
		}
	}, [delay]);
}
